import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/private/services/api.service';

export interface CrmIntegrationState {
  provider: string;
  status: 'connected' | 'available' | 'native';
  last_synced_at?: string | null;
  last_sync_count?: number | null;
  last_sync_leads?: number | null;
  last_sync_contacts?: number | null;
  has_credentials?: boolean;
}

export interface CrmLeadSyncResult {
  created: number;
  leads_created?: number;
  contacts_created?: number;
  skipped: number;
  fetched: number;
  source?: string;
  last_synced_at?: string;
  last_sync_leads?: number;
  last_sync_contacts?: number;
}

function unwrap<T>(body: any): T {
  if (body && typeof body === 'object' && body.data != null) {
    return body.data as T;
  }
  return body as T;
}

@Injectable({ providedIn: 'root' })
export class CrmIntegrationService {
  constructor(private api: ApiService) {}

  getCrmIntegrations(client_id: number | string): Observable<CrmIntegrationState[]> {
    return this.api.post<any>(environment.getCrmIntegrations, { client_id: Number(client_id) }).pipe(
      map((res) => unwrap<any>(res)?.integrations || []),
    );
  }

  saveCrmIntegration(
    client_id: number | string,
    provider: string,
    credentials: Record<string, string>,
  ): Observable<any> {
    return this.api.post<any>(environment.saveCrmIntegration, {
      client_id: Number(client_id),
      provider,
      credentials,
    });
  }

  testCrmIntegration(
    client_id: number | string,
    provider: string,
    credentials: Record<string, string>,
  ): Observable<any> {
    return this.api.post<any>(environment.testCrmIntegration, {
      client_id: Number(client_id),
      provider,
      credentials,
    });
  }

  disconnectCrmIntegration(client_id: number | string, provider: string): Observable<any> {
    return this.api.post<any>(environment.disconnectCrmIntegration, {
      client_id: Number(client_id),
      provider,
    });
  }

  syncCrmIntegrationLeads(
    client_id: number | string,
    provider: string,
    user_id?: number | string | null,
    options?: {
      sync_leads?: boolean;
      sync_contacts?: boolean;
      center_id?: number | string | null;
    },
  ): Observable<{ message?: string; data?: CrmLeadSyncResult } & CrmLeadSyncResult> {
    return this.api
      .post<any>(environment.syncCrmIntegrationLeads, {
        client_id: Number(client_id),
        provider,
        sync_leads: options?.sync_leads !== false,
        sync_contacts: options?.sync_contacts !== false,
        ...(user_id != null && user_id !== '' ? { user_id: Number(user_id) } : {}),
        ...(options?.center_id != null && options.center_id !== ''
          ? { center_id: Number(options.center_id) }
          : {}),
      })
      .pipe(
        map((res) => {
          const data = unwrap<CrmLeadSyncResult>(res);
          return { ...data, message: res?.message };
        }),
      );
  }
}
